/**
 * ── `lib:` URIs AND THE CDN LAYOUT (plan §2.1, §2.3, §13.1; ADR-0003 §1, §2) ────────────────────
 *
 * Two pure string folds, and the reason they are one module: the LAYOUT on the CDN
 * (`/library/catalog/latest.json`, `/library/catalog/<hash>.json`, `/library/blobs/<sha256>.<ext>`)
 * is stated ONCE here, and `publish:library` (plan §7) writes the same shape. A second copy of the
 * path join in the publish script is how the client and the bucket come to disagree about where an
 * asset lives — with no error, only a 404 nobody can explain.
 *
 * ── ⭐ P1.5 · THE TRANSFER UNIT IS A BLOB, NOT AN ASSET DIRECTORY (plan §13.1) ────────────────────
 *
 * P1 served `/library/assets/<id>/<hash>/<file>`, which keyed TRANSFER to DECLARATION: byte-
 * identical content under two ids shipped twice and no amount of refcounting could recover it.
 * The per-asset directory is DELETED, not dual-written — one layout, one migration, taken while
 * nothing external serves this. `assetFileUrl` and `parseAssetFileUrl` are RETIRED with it; a
 * caller that wants a URL asks the catalog for a role's blob and calls `blobUrl`.
 *
 * The catalog pointer and the catalog URL are UNCHANGED: the catalog is still the one mutable
 * pointer at one immutable body, and only the thing it points AT changed shape.
 *
 * `lib:<id>` is the ONE scheme a world's `assets[].uri` gains (§2.3). `parseLibUri` is STRICT —
 * exactly `lib:` plus a kebab id — because the id becomes a catalog key and a lenient parser here
 * is an open door there.
 *
 * PURE (`PURE_DIRS`): strings in, strings out.
 */

import { ASSET_ID_PATTERN } from './catalog';

export const LIB_SCHEME = 'lib:' as const;
export const LIBRARY_ROOT = '/library' as const;
/** The one directory every byte the library ships lives in. `authoring.ts` mirrors this word. */
export const LIBRARY_BLOBS_SEGMENT = 'blobs' as const;

/** `lib:solar-system-01` → `solar-system-01`; anything else → `null`. No trimming, no case folding. */
export function parseLibUri(uri: string): string | null {
  if (!uri.startsWith(LIB_SCHEME)) return null;
  const id = uri.slice(LIB_SCHEME.length);
  return ASSET_ID_PATTERN.test(id) ? id : null;
}

export function isLibUri(uri: string): boolean {
  return parseLibUri(uri) !== null;
}

/** `solar-system-01` → `lib:solar-system-01`. Throws on a non-kebab id: callers build these from catalog ids. */
export function libUri(id: string): string {
  if (!ASSET_ID_PATTERN.test(id)) throw new Error(`libUri: '${id}' is not a kebab-case asset id`);
  return `${LIB_SCHEME}${id}`;
}

/**
 * `origin` is `''` for same-origin (dev — plan §2.1) or `https://cdn.example` in production;
 * a trailing slash is tolerated and dropped so `'https://x/'` and `'https://x'` build one URL.
 */
function base(origin: string): string {
  return origin.endsWith('/') ? origin.slice(0, -1) : origin;
}

/**
 * `<origin>/library/blobs/<sha256>.<ext>` — immutable, `max-age=31536000`. The sha256 and the ext
 * come from a catalog `files[role]` entry, which the schema has already pattern-fenced, so this is
 * a join and nothing more: it does not sanitise, because the place to refuse a hostile string is
 * the parser (`AssetFileSchema`), not every caller of the path builder.
 */
export function blobUrl(origin: string, sha256: string, ext: string): string {
  return `${base(origin)}${LIBRARY_ROOT}/${LIBRARY_BLOBS_SEGMENT}/${sha256}.${ext}`;
}

/** `<origin>/library/catalog/latest.json` — short max-age; the only mutable URL in the layout. */
export function catalogPointerUrl(origin: string): string {
  return `${base(origin)}${LIBRARY_ROOT}/catalog/latest.json`;
}

/** `<origin>/library/catalog/<hash>.json` — immutable. */
export function catalogUrl(origin: string, hash: string): string {
  return `${base(origin)}${LIBRARY_ROOT}/catalog/${hash}.json`;
}
